/**
 * Transient-failure retry helper for Axios instances.
 *
 * Most 5xx responses from our backend are **transient** — they come
 * from the 2–5 s window during a rolling deploy where the ingress
 * still routes to a pod that's draining (SIGTERM sent, new pod not
 * yet ready), or from a pod briefly unresponsive during a crash
 * recovery. A single retry ~1 s later almost always succeeds. Without
 * a retry layer, every one of these transients becomes a user-visible
 * banner/redirect/error-toast.
 *
 * What this does:
 *
 * - Wraps an Axios instance's response-error chain.
 * - For **idempotent** requests (GET/HEAD/OPTIONS) that failed with
 *   503/502/504 or a bare network error, retries up to 3 times with
 *   exponential backoff + jitter (300 ms → 900 ms → 2700 ms).
 * - Never retries mutations (POST/PUT/PATCH/DELETE) — those might
 *   have partially succeeded server-side, and a retry would risk
 *   double-submission. The caller can opt into retry for specific
 *   endpoints via `request.config.metadata.allowRetry = true`.
 * - If all retries fail the error propagates unchanged so the
 *   existing event-dispatch / banner flow still kicks in.
 *
 * Applied via `installTransientRetry(axiosInstance)`. The response
 * interceptor is added **before** the existing error handlers, so
 * by the time control reaches them every retry has already been
 * exhausted.
 */

import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

// The codebase already augments AxiosRequestConfig elsewhere (e.g.
// ApiClient.ts stores startTime on `metadata`, custom-axios-instance
// uses `__requestStartTime`). To avoid fighting that with an
// `extends` that re-declares `metadata`, we keep the retry-specific
// fields under a runtime marker and read them via a loose cast.
// This trades a little type-safety at the read sites for zero
// friction with existing augmentations.
type RetryConfigShape = AxiosRequestConfig & {
  __retryAttempt?: number;
  metadata?: Record<string, unknown> & { allowRetry?: boolean };
};

// Status codes worth retrying. 503 is the rolling-deploy classic;
// 502 happens on nginx/ingress hiccups; 504 on upstream gateway
// timeouts. 500 is deliberately NOT in this list — a 500 usually
// indicates an actual server bug that won't resolve itself, and
// retrying masks the issue from observability.
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

// Idempotent by HTTP semantics. Methods not in this set are only
// retried when the caller opts in explicitly.
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 300;
const DEFAULT_JITTER_MS = 150;

function shouldRetry(error: AxiosError): boolean {
  const config = error.config as RetryConfigShape | undefined;
  if (!config) return false;

  const attempt = config.__retryAttempt ?? 0;
  if (attempt >= DEFAULT_MAX_RETRIES) return false;

  const method = (config.method || 'get').toLowerCase();
  const explicitOptIn = config.metadata?.allowRetry === true;
  if (!IDEMPOTENT_METHODS.has(method) && !explicitOptIn) return false;

  // Network error (no response) — bare transport failure, retry.
  if (!error.response) {
    const code = error.code;
    const message = error.message || '';
    if (
      code === 'ECONNABORTED' ||
      code === 'ETIMEDOUT' ||
      code === 'ERR_NETWORK' ||
      message === 'Network Error' ||
      message.toLowerCase().includes('timeout') ||
      message.toLowerCase().includes('network connection failed')
    ) {
      return true;
    }
    return false;
  }

  return RETRYABLE_STATUSES.has(error.response.status);
}

function computeDelay(attempt: number): number {
  // attempt is 0-indexed: 0 → ~300 ms, 1 → ~900 ms, 2 → ~2700 ms.
  const base = DEFAULT_BASE_DELAY_MS * Math.pow(3, attempt);
  const jitter = Math.floor(Math.random() * DEFAULT_JITTER_MS);
  return base + jitter;
}

export function installTransientRetry(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (!shouldRetry(error)) {
        return Promise.reject(error);
      }
      const config = error.config as RetryConfigShape;
      config.__retryAttempt = (config.__retryAttempt ?? 0) + 1;
      const delayMs = computeDelay(config.__retryAttempt - 1);

      await new Promise((resolve) => setTimeout(resolve, delayMs));

      return client.request(config as AxiosRequestConfig);
    },
  );
}

// Exposed for tests so the backoff schedule is explicit and
// locked down — silently changing the delay values would erode
// the UX guarantee (retries must complete inside the window a
// user would accept waiting for an idempotent fetch).
export const _test = {
  shouldRetry,
  computeDelay,
  RETRYABLE_STATUSES,
  IDEMPOTENT_METHODS,
  DEFAULT_MAX_RETRIES,
};
